import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, UserCog } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ImpersonationStartDialog } from '@/components/platform/ImpersonationStartDialog';
import { usePlatformTenant } from '@/hooks/usePlatformTenant';

const TIER_LABELS: Record<string, string> = {
  FREE: 'Free',
  BASIC: 'Basic',
  PROFESSIONAL: 'Professional',
  ENTERPRISE: 'Enterprise',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  trial: 'bg-blue-100 text-blue-800',
};

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MEMBER: 'Member',
  VIEWER: 'Viewer',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE');
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE');
}

function formatNumber(n: number): string {
  return n.toLocaleString('de-DE');
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] ?? 'bg-muted text-muted-foreground'}`}
    >
      {status}
    </span>
  );
}

export function PlatformTenantDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error, refetch } = usePlatformTenant(id);
  const [impersonateOpen, setImpersonateOpen] = useState(false);

  return (
    <div>
      <div className="mb-2">
        <Link
          to="/platform/tenants"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Zurück zur Tenant-Liste
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{data?.name ?? 'Tenant-Detail'}</h1>
          <p className="mt-2 text-muted-foreground">
            {data?.slug ? `Slug: ${data.slug}` : 'Stammdaten, Abrechnung und Nutzung dieses Tenants.'}
          </p>
        </div>
        {data && (
          <Button onClick={() => setImpersonateOpen(true)}>
            <UserCog className="mr-2 h-4 w-4" />
            Impersonieren
          </Button>
        )}
      </div>

      {data && (
        <ImpersonationStartDialog
          open={impersonateOpen}
          onOpenChange={setImpersonateOpen}
          tenantId={data.id}
          tenantName={data.name}
        />
      )}

      {isLoading ? (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : isError ? (
        <div className="mt-6 flex flex-col items-center gap-3 py-8">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>{error instanceof Error ? error.message : 'Fehler beim Laden des Tenants'}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Erneut versuchen
          </Button>
        </div>
      ) : !data ? (
        <p className="mt-6 py-8 text-center text-sm text-muted-foreground">Tenant nicht gefunden.</p>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Stammdaten */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Stammdaten</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-3 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Name</dt>
                <dd className="col-span-2 font-medium">{data.name}</dd>
                <dt className="text-muted-foreground">Slug</dt>
                <dd className="col-span-2 font-mono text-xs">{data.slug}</dd>
                <dt className="text-muted-foreground">Tenant-ID</dt>
                <dd className="col-span-2 font-mono text-xs">{data.id}</dd>
                <dt className="text-muted-foreground">Erstellt</dt>
                <dd className="col-span-2">{formatDate(data.createdAt)}</dd>
                <dt className="text-muted-foreground">Aktualisiert</dt>
                <dd className="col-span-2">{formatDate(data.updatedAt)}</dd>
              </dl>
            </CardContent>
          </Card>

          {/* Billing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Abrechnung</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-3 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Tier</dt>
                <dd className="col-span-2 font-medium">
                  {TIER_LABELS[data.subscriptionTier] ?? data.subscriptionTier}
                </dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd className="col-span-2">
                  <StatusBadge status={data.status} />
                </dd>
              </dl>
            </CardContent>
          </Card>

          {/* Metriken */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Nutzung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Quizzes</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">{formatNumber(data.stats.quizCount)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Aktive Quizzes</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">{formatNumber(data.stats.activeQuizCount)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Produkte</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">{formatNumber(data.stats.productCount)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Users</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">{formatNumber(data.stats.userCount)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Users */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">
                Benutzer
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({formatNumber(data.users.length)})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.users.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">Keine Benutzer.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 font-medium">Name</th>
                        <th className="pb-2 font-medium">E-Mail</th>
                        <th className="pb-2 font-medium">Rolle</th>
                        <th className="pb-2 font-medium">Aktiv</th>
                        <th className="pb-2 font-medium">Letzter Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.users.map((u) => {
                        const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
                        return (
                          <tr key={u.id} className="border-b last:border-0">
                            <td className="py-2">{fullName || <span className="text-muted-foreground">—</span>}</td>
                            <td className="py-2 font-mono text-xs">{u.email}</td>
                            <td className="py-2">{ROLE_LABELS[u.role] ?? u.role}</td>
                            <td className="py-2">{u.isActive ? 'Ja' : 'Nein'}</td>
                            <td className="py-2 text-muted-foreground">
                              {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
